<template>
  <div>
    <el-dialog title="管理端场景试聊" :visible.sync="visible" width="920px" :before-close="closeTrial" :close-on-click-modal="false">
      <el-alert title="试聊仅供场景编辑人员验证草稿，记录与正式训练分开，不改变学员授权。" type="info" :closable="false" class="mb10" />
      <el-alert v-if="error" :title="error" type="error" :closable="false" class="mb10" />
      <div v-if="!session" v-loading="sceneLoading">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="场景">{{ scene.title }}</el-descriptions-item>
          <el-descriptions-item label="最大轮次">{{ scene.maxTurns || '-' }}</el-descriptions-item>
          <el-descriptions-item label="学员岗位">{{ scene.jobPosition || '-' }}</el-descriptions-item>
          <el-descriptions-item label="训练目标">{{ scene.coreTaskGoal || '-' }}</el-descriptions-item>
          <el-descriptions-item label="AI角色身份">{{ scene.roleIdentity || '-' }}</el-descriptions-item>
          <el-descriptions-item label="场景背景">{{ scene.backgroundContext || '-' }}</el-descriptions-item>
        </el-descriptions>
        <el-button class="mt10" type="primary" :loading="starting" :disabled="!sceneLoaded" @click="start">开始试聊</el-button>
        <el-button class="mt10" @click="configureScore">查看 / 配置评分表</el-button>
      </div>
      <template v-else>
        <div class="trial-meta">
          <strong>{{ (session.scene && session.scene.title) || scene.title }}</strong>
          <span>第 {{ session.turn }} / {{ session.maxTurns }} 轮</span>
          <el-tag size="small">{{ statusLabel(session.status) }}</el-tag>
          <span>用时 {{ duration(session.elapsedSec) }}</span>
          <span v-if="session.mode === 2 && session.status === 1">剩余 {{ duration(remaining) }}</span>
          <span v-if="session.endReason">结束原因：{{ endLabel(session.endReason) }}</span>
        </div>
        <div ref="messages" class="trial-messages">
          <div v-for="(m, index) in messages" :key="m.id || index" class="trial-message" :class="{ user: m.role === 'user' }">
            <div class="message-label">{{ m.role === 'user' ? '试聊人员' : 'AI客户' }} <span v-if="m.createdAt">{{ timestamp(m.createdAt) }}</span></div>
            <div>{{ m.content || (m.streaming ? 'AI正在回复…' : '') }}</div>
          </div>
        </div>
        <div v-if="session.status === 1" class="trial-input">
          <el-input v-model="draft" type="textarea" :rows="2" maxlength="500" placeholder="输入学员话术" :disabled="sending || recoveryRequired" />
          <el-button type="primary" :loading="sending" :disabled="!draft.trim() || recoveryRequired" @click="send">发送</el-button>
          <el-button :disabled="sending || ending" @click="end">结束并评分</el-button>
          <el-button :disabled="sending" @click="reload">重新读取会话</el-button>
        </div>
        <template v-if="session.status !== 1">
          <el-alert v-if="report.state === 'evaluating'" title="试聊已结束，正在评分，可稍后从预览记录查看结果。" type="info" :closable="false" class="mt10" />
          <el-alert v-if="report.state === 'failed'" :title="report.failureReason || report.errorReason || '本次试聊评分失败，请核对场景评分表和服务状态。'" type="error" :closable="false" class="mt10" />
          <el-alert v-if="report.state === 'abandoned'" title="本次试聊已放弃，未生成评分。对话仍保留在预览记录中。" type="info" :closable="false" class="mt10" />
          <el-card v-if="report.state === 'done'" class="mt10">
            <div slot="header">试聊测评：{{ report.totalScore }} / 100 <el-tag v-if="report.passed === true" type="success">通过</el-tag><el-tag v-else-if="report.passed === false" type="info">未通过</el-tag><span v-else>通过状态未确认</span></div>
            <p>基础分 {{ report.baseScore }} + 难度分 {{ report.difficultyScore }}；通关线 {{ report.passScore }} 分</p>
            <el-table :data="report.items || []" border size="small">
              <el-table-column prop="name" label="考核项" min-width="140" />
              <el-table-column label="得分" width="90"><template slot-scope="{row}">{{ row.score }} / {{ row.maxScore }}</template></el-table-column>
              <el-table-column prop="evidence" label="评分依据" min-width="220" />
              <el-table-column prop="deductionReason" label="扣分原因" min-width="180" />
            </el-table>
            <p>{{ report.coachComment }}</p>
            <p v-for="(s, i) in report.suggestions || []" :key="i">{{ i + 1 }}. {{ s }}</p>
          </el-card>
          <el-button class="mt10" @click="loadReport">重新读取试聊评分</el-button>
        </template>
      </template>
      <div slot="footer"><el-button :disabled="starting || ending" @click="closeTrial()">关闭</el-button></div>
    </el-dialog>

    <el-dialog title="本场景预览记录（仅自己的试聊）" :visible.sync="historyVisible" width="1000px" :close-on-click-modal="false">
      <el-alert title="此处只展示当前管理人员的试聊记录，正式学员训练请使用“正式记录”入口。" type="info" :closable="false" class="mb10" />
      <el-alert v-if="historyError" :title="historyError" type="error" :closable="false" class="mb10"><el-button type="text" @click="loadHistory">重新加载</el-button></el-alert>
      <el-table :data="historyRows" v-loading="historyLoading" border size="small">
        <el-table-column prop="sessionId" label="试聊编号" width="100" />
        <el-table-column label="开始时间" width="180"><template slot-scope="{row}">{{ timestamp(row.startedAt) }}</template></el-table-column>
        <el-table-column label="结束时间" width="180"><template slot-scope="{row}">{{ timestamp(row.endedAt) }}</template></el-table-column>
        <el-table-column label="状态" width="100"><template slot-scope="{row}">{{ statusLabel(row.status) }}</template></el-table-column>
        <el-table-column label="用时" width="105"><template slot-scope="{row}">{{ duration(row.elapsedSec) }}</template></el-table-column>
        <el-table-column label="得分" width="70"><template slot-scope="{row}">{{ row.totalScore == null ? '-' : row.totalScore }}</template></el-table-column>
        <el-table-column label="操作" min-width="105"><template slot-scope="{row}"><el-button type="text" @click="viewHistory(row)">{{ row.status === 1 ? '继续试聊' : '对话 / 评分' }}</el-button></template></el-table-column>
      </el-table>
      <el-pagination class="mt10" :current-page.sync="historyPage" :page-size="10" :total="historyTotal" layout="total, prev, pager, next" @current-change="loadHistory" />
      <div slot="footer"><el-button @click="historyVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import trialApi, { streamTrialChat } from '@/api/aiTrain/trial'
import sceneApi from '@/api/aiTrain/scene'
import { listRows } from '@/utils/ai-train-data'

export default {
  name: 'AiTrainAdminTrialPanel',
  data() {
    return {
      visible: false, scene: {}, sceneLoading: false, sceneLoaded: false, starting: false,
      session: null, messages: [], draft: '', sending: false, ending: false, error: '',
      recoveryRequired: false, sequence: 0, stream: null, heartbeatTimer: null,
      heartbeatPending: false, reportTimer: null, report: {}, reportAttempts: 0, clock: Date.now(),
      clockTimer: null, historyVisible: false, historyScene: {}, historyRows: [], historyPage: 1,
      historyTotal: 0, historyLoading: false, historyError: '', historySequence: 0
    }
  },
  computed: {
    remaining() {
      if (!this.session || !this.session.deadlineAt) return null
      const deadline = dayjs(this.session.deadlineAt).valueOf()
      return Number.isFinite(deadline) ? Math.max(0, Math.ceil((deadline - this.clock) / 1000)) : null
    }
  },
  beforeDestroy() { ++this.sequence; this.cleanup() },
  deactivated() { ++this.sequence; ++this.historySequence; this.cleanup(); this.visible = false; this.historyVisible = false },
  methods: {
    timestamp(value) { if (!value) return '-'; const time = dayjs(value); return time.isValid() ? time.format('YYYY-MM-DD HH:mm:ss') : '-' },
    duration(value) {
      if (value == null || !Number.isFinite(Number(value))) return '-'
      const sec = Math.max(0, Math.floor(Number(value))), pad = v => String(v).padStart(2, '0')
      return pad(Math.floor(sec / 3600)) + ':' + pad(Math.floor(sec % 3600 / 60)) + ':' + pad(sec % 60)
    },
    statusLabel(status) { return { 1: '进行中', 2: '正在评分', 3: '已评分', 4: '评分失败', 5: '已放弃' }[status] || '状态未确认' },
    endLabel(reason) { return { 1: '主动结束', 2: '时间结束', 3: '退出提交', 4: '连接中断', 5: '系统结束', 6: 'AI结束', 7: '轮数上限' }[reason] || '已结束' },
    open(row) {
      ++this.sequence; this.cleanup(); this.visible = true; this.scene = { ...row }
      this.session = null; this.messages = []; this.report = {}; this.draft = ''; this.error = ''
      this.starting = false; this.sending = false; this.ending = false; this.recoveryRequired = false
      const sequence = this.sequence; this.sceneLoading = true; this.sceneLoaded = false
      sceneApi.fetchById(row.id).then(res => {
        if (sequence !== this.sequence) return
        if (!res.data || Number(res.data.id) !== Number(row.id)) throw new Error('场景读取结果未确认')
        this.scene = res.data; this.sceneLoaded = true
      }).catch(() => { if (sequence === this.sequence) this.error = '场景读取失败，请重新打开试聊。' })
        .finally(() => { if (sequence === this.sequence) this.sceneLoading = false })
    },
    async start() {
      if (this.starting || !this.sceneLoaded) return
      this.starting = true; this.error = ''; const sequence = this.sequence
      try {
        const res = await trialApi.start(this.scene.id)
        if (sequence !== this.sequence) return
        if (!res.data || res.data.preview !== true || !Number.isSafeInteger(Number(res.data.sessionId)) || Number(res.data.sessionId) <= 0) throw new Error('试聊创建结果未确认，请重新读取记录核对')
        this.session = { ...res.data, status: 1, maxTurns: this.scene.maxTurns }
        await this.reload(); if (sequence === this.sequence && this.session && this.session.status === 1 && !this.recoveryRequired) this.startTimers()
      } catch (e) { if (sequence === this.sequence) this.error = e.message || '创建试聊失败' }
      finally { if (sequence === this.sequence) this.starting = false }
    },
    async reload() {
      if (!this.session) return
      const sequence = this.sequence, id = this.session.sessionId
      try {
        const res = await trialApi.get(id)
        if (sequence !== this.sequence || !this.session || id !== this.session.sessionId) return
        if (!res.data || res.data.preview !== true || Number(res.data.sessionId) !== Number(id)
          || !Number.isInteger(res.data.status) || res.data.status < 1 || res.data.status > 5
          || !Number.isInteger(res.data.turn) || res.data.turn < 0) throw new Error('返回的试聊归属或状态未确认')
        this.session = res.data; this.messages = listRows(res.data.messages || [])
        this.recoveryRequired = false; this.error = ''; this.scroll()
        if (this.session.status !== 1) {
          if (this.stream) this.stream.abort()
          this.sending = false; this.stopHeartbeat(); await this.loadReport()
        }
      } catch (e) { if (sequence === this.sequence) { this.error = e.message || '试聊读取失败'; this.recoveryRequired = true } }
    },
    send() {
      if (!this.session || this.session.status !== 1 || this.sending || this.recoveryRequired || !this.draft.trim()) return
      const content = this.draft.trim(), sequence = this.sequence, id = this.session.sessionId
      this.draft = ''; this.error = ''; this.sending = true
      this.messages.push({ role: 'user', content }); const message = { role: 'assistant', content: '', streaming: true }
      this.messages.push(message)
      let settled = false
      this.stream = streamTrialChat(id, content, {
        onDelta: text => { if (!settled && sequence === this.sequence && this.session && this.session.status === 1) { message.content += text; this.scroll() } },
        onDone: data => {
          if (settled || sequence !== this.sequence) return
          settled = true
          this.sending = false; message.streaming = false
          if (!Number.isInteger(data.turn) || !Number.isInteger(data.status)) { this.error = '完成状态未确认，请重新读取试聊'; this.recoveryRequired = true; return }
          this.recoveryRequired = true
          this.reload()
        },
        onError: text => { if (!settled && sequence === this.sequence) { settled = true; this.sending = false; this.error = text; this.recoveryRequired = true; message.streaming = false } }
      })
    },
    async end() {
      if (!this.session || this.session.status !== 1 || this.ending || this.sending) return
      this.ending = true; const sequence = this.sequence
      try { await trialApi.end(this.session.sessionId); if (sequence === this.sequence) await this.reload() }
      catch (e) { if (sequence === this.sequence) this.error = e.message || '结束试聊失败' }
      finally { if (sequence === this.sequence) this.ending = false }
    },
    startTimers() {
      this.stopHeartbeat()
      this.clock = Date.now()
      this.clockTimer = setInterval(() => { this.clock = Date.now() }, 1000)
      this.heartbeatTimer = setInterval(() => this.heartbeat(), 10000)
    },
    async heartbeat() {
      if (!this.session || this.session.status !== 1 || this.heartbeatPending || !this.visible) return
      this.heartbeatPending = true; const sequence = this.sequence, id = this.session.sessionId
      try {
        const res = await trialApi.heartbeat(id, document.visibilityState === 'visible')
        if (sequence !== this.sequence || !this.session || id !== this.session.sessionId) return
        if (!res.data || !Number.isInteger(res.data.status) || res.data.status < 1 || res.data.status > 5
          || !Number.isInteger(res.data.elapsedSec) || res.data.elapsedSec < 0) throw new Error('试聊状态未确认，请重新读取会话')
        this.session.elapsedSec = res.data.elapsedSec
        if (res.data.status !== 1) await this.reload()
      } catch (e) { if (sequence === this.sequence) { this.error = e.message || '试聊状态核对失败，请重新读取会话'; this.recoveryRequired = true } }
      finally { if (sequence === this.sequence) this.heartbeatPending = false }
    },
    async loadReport() {
      if (!this.visible || !this.session || this.session.status === 1) return
      clearTimeout(this.reportTimer); const sequence = this.sequence, id = this.session.sessionId
      try {
        const res = await trialApi.evaluation(id)
        if (sequence !== this.sequence || !this.session || id !== this.session.sessionId) return
        this.report = res.data || {}
        if (this.report.state === 'evaluating' && ++this.reportAttempts < 24) this.reportTimer = setTimeout(() => this.loadReport(), 5000)
      } catch (e) { if (sequence === this.sequence) this.error = e.message || '试聊评分读取失败' }
    },
    closeTrial(done) {
      if (this.starting || this.ending) return
      const close = () => { ++this.sequence; this.cleanup(); this.visible = false; if (typeof done === 'function') done() }
      if (this.session && this.session.status === 1) {
        const sequence = this.sequence, id = this.session.sessionId
        this.$common.confirm('结束当前试聊并关闭？试聊记录仍保留在预览记录中。').then(async() => {
          if (sequence !== this.sequence || !this.session || id !== this.session.sessionId) return
          if (this.stream) this.stream.abort(); this.sending = false
          await this.end(); if (sequence === this.sequence && this.session && id === this.session.sessionId && this.session.status !== 1) close()
        }).catch(() => {})
      } else close()
    },
    configureScore() {
      const scene = { ...this.scene }
      this.closeTrial(() => this.$emit('configure-score', scene))
    },
    stopHeartbeat() { clearInterval(this.heartbeatTimer); clearInterval(this.clockTimer); this.heartbeatTimer = null; this.clockTimer = null },
    cleanup() { this.stopHeartbeat(); clearTimeout(this.reportTimer); if (this.stream) this.stream.abort(); this.stream = null; this.heartbeatPending = false; this.reportAttempts = 0 },
    scroll() { this.$nextTick(() => { if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight }) },
    history(row) { this.historyScene = { ...row }; this.historyPage = 1; this.historyRows = []; this.historyVisible = true; this.loadHistory() },
    async loadHistory() {
      const sequence = ++this.historySequence; this.historyLoading = true; this.historyError = ''
      try {
        const res = await trialApi.history(this.historyScene.id, this.historyPage, 10)
        if (sequence !== this.historySequence) return
        const rows = listRows(res.data)
        if (rows.some(r => r.preview !== true || Number(r.sceneId) !== Number(this.historyScene.id))) throw new Error('预览记录范围未确认')
        this.historyRows = rows; this.historyTotal = res.data.total || 0
      } catch (e) { if (sequence === this.historySequence) { this.historyRows = []; this.historyError = e.message || '预览记录加载失败' } }
      finally { if (sequence === this.historySequence) this.historyLoading = false }
    },
    async viewHistory(row) {
      ++this.sequence; this.cleanup(); this.historyVisible = false; this.visible = true
      this.scene = { ...this.historyScene }; this.session = { sessionId: row.sessionId }; this.messages = []
      this.report = {}; this.error = ''; this.draft = ''; this.sending = false; this.recoveryRequired = false
      this.starting = false; this.ending = false; this.sceneLoading = false; this.sceneLoaded = false
      await this.reload(); if (this.session && this.session.status === 1) this.startTimers()
    }
  }
}
</script>

<style scoped>
.mt10 { margin-top: 10px; }
.mb10 { margin-bottom: 10px; }
.trial-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin: 12px 0; }
.trial-messages { max-height: 360px; overflow-y: auto; border: 1px solid #ebeef5; padding: 12px; border-radius: 4px; }
.trial-message { max-width: 85%; padding: 10px 14px; border-radius: 6px; background: #f4f4f5; margin-bottom: 12px; white-space: pre-wrap; line-height: 1.6; }
.trial-message.user { margin-left: auto; background: #ecf5ff; }
.message-label { font-size: 12px; color: #909399; margin-bottom: 4px; }
.trial-input { display: flex; align-items: center; gap: 10px; margin-top: 12px; }
.trial-input .el-input { flex: 1; }
.trial-input .el-button { margin-left: 0; }
</style>
